import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  ChevronDown,
  Clock3,
  Luggage,
  Plane,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import SwHeader from './components/SwHeader';
import SwFooter from './components/SwFooter';
import SwCallBar from './components/SwCallBar';
import AirportInput from './components/AirportInput';
import { airportCode } from './airportOptions';
import { DUMMY_FLIGHTS } from './dummyFlights';
import { SW_BRAND, SW_LOGO_BY } from './config';
import { AirlineLandingProvider, useAirlineLanding } from './AirlineContext';
import { airlineThemeClass, getAirlineBySlug } from './airlines';
import './southwest.css';
import './flight-search.css';
import './airline-themes.css';

function formatDate(value) {
  if (!value) return 'Flexible date';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function FlightSearchInner() {
  const { path, airline } = useAirlineLanding();
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState('recommended');
  const [nonstopOnly, setNonstopOnly] = useState(params.get('nonstop') === 'true');
  const [maxPrice, setMaxPrice] = useState(350);
  const [from, setFrom] = useState(params.get('from') || 'New York (JFK)');
  const [to, setTo] = useState(params.get('to') || 'Los Angeles (LAX)');

  const fromCode = airportCode(params.get('from') || from, 'JFK');
  const toCode = airportCode(params.get('to') || to, 'LAX');
  const passengers = params.get('passengers') || '1';
  const cabin = params.get('cabin') || 'economy';
  const departDate = params.get('depart') || '';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${fromCode} to ${toCode} Flight Options | ${SW_BRAND} by ${SW_LOGO_BY}`;
    document.body.classList.add('swp-standalone');

    return () => {
      document.title = previousTitle;
      document.body.classList.remove('swp-standalone');
    };
  }, [fromCode, toCode]);

  const flights = useMemo(() => {
    const filtered = DUMMY_FLIGHTS.filter(
      (flight) => flight.price <= maxPrice && (!nonstopOnly || flight.stops === 0),
    );

    return [...filtered].sort((a, b) => {
      if (sort === 'price') return a.price - b.price;
      if (sort === 'duration') return Number.parseInt(a.duration, 10) - Number.parseInt(b.duration, 10);
      if (sort === 'departure') return a.depart.localeCompare(b.depart);
      return (a.stops - b.stops) || (a.price - b.price);
    });
  }, [maxPrice, nonstopOnly, sort]);

  const submitRoute = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    next.set('from', from);
    next.set('to', to);
    next.set('nonstop', String(nonstopOnly));
    setParams(next);
  };

  return (
    <div className={airlineThemeClass('swp swr', airline)}>
      <SwHeader />

      <main>
        <section className="swr-hero">
          <div className="swp-shell">
            <div className="swr-breadcrumb">
              <Link to={path}>{airline.shortName} flights</Link>
              <span>/</span>
              <span>Search results</span>
            </div>

            <div className="swr-hero__row">
              <div>
                <p className="swr-eyebrow">Available flight options</p>
                <h1>
                  {fromCode} <ArrowRight size={24} aria-hidden /> {toCode}
                </h1>
                <p>
                  {formatDate(departDate)} · {passengers} passenger
                  {passengers === '1' ? '' : 's'} · {cabin}
                </p>
              </div>
              <div className="swr-hero__trust">
                <ShieldCheck size={20} aria-hidden />
                <span>
                  <strong>Independent search</strong>
                  Prices shown are sample fares
                </span>
              </div>
            </div>

            <form className="swr-modify" onSubmit={submitRoute}>
              <AirportInput
                id="swr-from"
                label="From"
                value={from}
                onChange={setFrom}
              />
              <AirportInput id="swr-to" label="To" value={to} onChange={setTo} />
              <button className="swp-cta" type="submit">
                Update search
              </button>
            </form>
          </div>
        </section>

        <section className="swr-results">
          <div className="swp-shell swr-layout">
            <aside className="swr-filters">
              <div className="swr-filters__title">
                <SlidersHorizontal size={18} aria-hidden />
                <h2>Filters</h2>
              </div>

              <div className="swr-filter">
                <h3>Stops</h3>
                <label className="swr-check">
                  <input
                    type="checkbox"
                    checked={nonstopOnly}
                    onChange={(event) => setNonstopOnly(event.target.checked)}
                  />
                  Nonstop only
                </label>
              </div>

              <div className="swr-filter">
                <h3>Maximum price</h3>
                <input
                  className="swr-range"
                  type="range"
                  min="175"
                  max="350"
                  step="5"
                  value={maxPrice}
                  onChange={(event) => setMaxPrice(Number(event.target.value))}
                />
                <div className="swr-range__labels">
                  <span>$175</span>
                  <strong>${maxPrice}</strong>
                </div>
              </div>

              <div className="swr-filter">
                <h3>Included</h3>
                <ul className="swr-included">
                  <li><Luggage size={15} aria-hidden /> Personal item</li>
                  <li><CalendarDays size={15} aria-hidden /> 24-hour cancellation</li>
                  <li><ShieldCheck size={15} aria-hidden /> Fare confirmation</li>
                </ul>
              </div>
            </aside>

            <div className="swr-list">
              <div className="swr-list__top">
                <div>
                  <strong>{flights.length} sample flights</strong>
                  <span>Fares are illustrative and not live inventory.</span>
                </div>
                <label className="swr-sort">
                  Sort by
                  <span>
                    <select value={sort} onChange={(event) => setSort(event.target.value)}>
                      <option value="recommended">Recommended</option>
                      <option value="price">Lowest price</option>
                      <option value="duration">Shortest duration</option>
                      <option value="departure">Earliest departure</option>
                    </select>
                    <ChevronDown size={16} aria-hidden />
                  </span>
                </label>
              </div>

              {flights.length ? (
                <div className="swr-cards">
                  {flights.map((flight) => (
                    <article className="swr-card" key={flight.id}>
                      <div className="swr-card__airline">
                        <span className="swr-card__logo">{flight.code}</span>
                        <span>
                          <strong>{flight.airline}</strong>
                          <small>{flight.id}</small>
                        </span>
                      </div>

                      <div className="swr-card__journey">
                        <div>
                          <strong>{flight.depart}</strong>
                          <span>{fromCode}</span>
                        </div>
                        <div className="swr-card__line">
                          <small><Clock3 size={13} aria-hidden /> {flight.duration}</small>
                          <span><Plane size={15} aria-hidden /></span>
                          <small>
                            {flight.stops === 0 ? 'Nonstop' : `${flight.stops} stop · ${flight.stopCode}`}
                          </small>
                        </div>
                        <div>
                          <strong>{flight.arrive}</strong>
                          <span>{toCode}</span>
                        </div>
                      </div>

                      <div className="swr-card__price">
                        <span className="swr-card__badge">{flight.badge}</span>
                        <small>from</small>
                        <strong>${flight.price}</strong>
                        <span>per traveller</span>
                        <button type="button" className="swp-cta">Select</button>
                        <small>{flight.seats} seats at this sample price</small>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="swr-empty">
                  <Plane size={28} aria-hidden />
                  <h2>No flights match these filters</h2>
                  <p>Raise the maximum price or include one-stop options.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <SwFooter />
      <SwCallBar />
    </div>
  );
}

export default function FlightSearchPage({ slug }) {
  if (!getAirlineBySlug(slug)) return null;

  return (
    <AirlineLandingProvider slug={slug}>
      <FlightSearchInner />
    </AirlineLandingProvider>
  );
}
